import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { QueryError } from 'errors'
import { MRT_ColumnFiltersState, MRT_PaginationState } from 'mantine-react-table'
import { Filterable } from 'types/index'
import {
  GroupedShortage,
  GroupedShortageTableItem,
  NewShortage,
  NewShortageOrder,
  Shortage,
  ShortageOrder,
  ShortageOrderTableItem,
  ShortageStatus,
  ShortageTableItem,
  UpdateShortage,
  UpdateShortageOrder,
} from 'types/shortage'
import { getShortageStatus } from 'utils'

export function useAddShortage() {
  const queryClient = useQueryClient()

  return useMutation<Shortage, QueryError, NewShortage>({
    mutationKey: ['addShortage'],
    mutationFn: async (shortage) => {
      return (await axios.post('/shortage', shortage)).data
    },
    meta: { successMessage: 'Brak zostały dodany.', closeModal: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shortages'] })
    },
  })
}

interface Params {
  columnFilters: MRT_ColumnFiltersState
  pagination: MRT_PaginationState
}

export function shortagesQuery(userId: string = '', { columnFilters, pagination }: Params) {
  const fetchUrl = new URL(`/shortage/${userId}`, window.location.origin)
  fetchUrl.searchParams.set('skip', `${pagination.pageIndex * pagination.pageSize}`)
  fetchUrl.searchParams.set('get', `${pagination.pageSize}`)
  fetchUrl.searchParams.set('filters', JSON.stringify(columnFilters ?? []))

  return queryOptions<Filterable<Shortage>, QueryError, Filterable<ShortageTableItem>>({
    queryKey: ['shortages', fetchUrl],
    queryFn: async () => {
      return (await axios.get(fetchUrl.pathname + fetchUrl.search)).data
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    select: ({ data, total }) => {
      return {
        data: data.map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          orderedAt: item.orderedAt ? new Date(item.orderedAt) : null,
          fulfilledAt: item.fulfilledAt ? new Date(item.fulfilledAt) : null,
        })),
        total,
      }
    },
  })
}

export function useShortages(userId: string = '', params: Params) {
  return useQuery<Filterable<Shortage>, QueryError, Filterable<ShortageTableItem>>(shortagesQuery(userId, params))
}

export function useGroupedShortages() {
  return useQuery<Array<GroupedShortage>, QueryError, Array<GroupedShortageTableItem>>({
    queryKey: ['shortages', 'grouped'],
    queryFn: async () => {
      return (await axios.get('/shortage/grouped')).data
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    select: (data) => {
      return data.map((group) => ({
        ...group,
        subRows: group.subRows.map((shortage) => ({
          ...shortage,
          createdAt: new Date(shortage.createdAt),
          orderedAt: shortage.orderedAt ? new Date(shortage.orderedAt) : null,
          fulfilledAt: shortage.fulfilledAt ? new Date(shortage.fulfilledAt) : null,
        })),
      }))
    },
  })
}

export function useDeleteShortage() {
  const queryClient = useQueryClient()

  return useMutation<void, QueryError, string>({
    mutationKey: ['deleteShortage'],
    mutationFn: async (id: string) => {
      await axios.delete(`/shortage/${id}`)
    },
    meta: { successMessage: 'Brak produktu został usunięty.' },
    onSuccess: (_, id: string) => {
      queryClient.invalidateQueries({ queryKey: ['shortages'] })
    },
  })
}

export function useUpdateShortage() {
  const queryClient = useQueryClient()

  return useMutation<Shortage, QueryError, UpdateShortage>({
    mutationKey: ['updateShortage'],
    mutationFn: async (shortage) => {
      return (await axios.put(`/shortage/${shortage.id}`, shortage)).data
    },
    meta: { successMessage: 'Brak został zaktualizowany.', closeModal: true },
    onSuccess: (updatedShortage: Shortage) => {
      queryClient.setQueriesData<Filterable<ShortageTableItem> | undefined>(
        { queryKey: ['shortages'], predicate: ({ queryKey }) => !queryKey.includes('grouped') },
        (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            data: oldData.data.map((item) => {
              if (item.id === updatedShortage.id) {
                return {
                  ...updatedShortage,
                  createdAt: new Date(updatedShortage.createdAt),
                  orderedAt: updatedShortage.orderedAt ? new Date(updatedShortage.orderedAt) : null,
                  fulfilledAt: updatedShortage.fulfilledAt ? new Date(updatedShortage.fulfilledAt) : null,
                }
              }
              return item
            }),
          }
        }
      )
      queryClient.invalidateQueries({ queryKey: ['shortages', 'grouped'] })
    },
  })
}

export function shortageOrdersQuery({ columnFilters, pagination }: Params) {
  const fetchUrl = new URL(`/shortage/order`, window.location.origin)
  fetchUrl.searchParams.set('skip', `${pagination.pageIndex * pagination.pageSize}`)
  fetchUrl.searchParams.set('get', `${pagination.pageSize}`)
  fetchUrl.searchParams.set('filters', JSON.stringify(columnFilters ?? []))

  return queryOptions<Filterable<ShortageOrder>, QueryError, Filterable<ShortageOrderTableItem>>({
    queryKey: ['shortages', 'orders', fetchUrl],
    queryFn: async () => {
      return (await axios.get(fetchUrl.pathname + fetchUrl.search)).data
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    select: ({ data, total }) => {
      return {
        data: data.map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          fulfilledAt: item.fulfilledAt ? new Date(item.fulfilledAt) : null,
        })),
        total,
      }
    },
  })
}

export function useShortageOrders(params: Params) {
  return useQuery<Filterable<ShortageOrder>, QueryError, Filterable<ShortageOrderTableItem>>(
    shortageOrdersQuery(params)
  )
}

export function useShortageOrderById(id: string) {
  return useQuery<ShortageOrder, QueryError, ShortageOrderTableItem>({
    queryKey: ['shortage', 'order', id],
    queryFn: async () => {
      return (await axios.get(`/shortage/order/${id}`)).data
    },
    staleTime: 15_000,
    select: (data) => ({
      ...data,
      createdAt: new Date(data.createdAt),
      fulfilledAt: data.fulfilledAt ? new Date(data.fulfilledAt) : null,
    }),
  })
}

export function useShortageOrder({
  product,
  feature,
  size,
  category,
  neededAmount,
}: Omit<GroupedShortage, 'manufacturer' | 'subRows'> & { neededAmount: number }) {
  const columnFilters: MRT_ColumnFiltersState = [
    { id: 'productId', value: product.id },
    { id: 'feature', value: feature },
    { id: 'size', value: size },
    { id: 'category', value: category },
    { id: 'fulfilledAt', value: null },
  ]

  return useQuery({
    ...shortageOrdersQuery({ columnFilters, pagination: { pageIndex: 0, pageSize: 100 } }),
    select: (data) => {
      return data.data.filter((order) => order.orderedAmount - order.shortageAmount >= neededAmount)
    },
    staleTime: 0,
  })
}

export function useOrderShortages() {
  const queryClient = useQueryClient()

  return useMutation<ShortageOrder, QueryError, NewShortageOrder>({
    mutationKey: ['orderShortages'],
    mutationFn: async (newShortageOrder) => {
      return (await axios.post('/shortage/order', newShortageOrder)).data
    },
    meta: { successMessage: 'Zamówienie zostało złożone.', closeModal: true },
    onSuccess: (newShortageOrder) => {
      console.log('newShortageOrder', newShortageOrder)
      queryClient.invalidateQueries({ queryKey: ['shortages'] })
      // add the new shortage order to the shortage orders query data
      queryClient.setQueriesData<Filterable<ShortageOrderTableItem> | undefined>(
        { queryKey: ['shortages', 'orders'] },
        (oldData) => {
          if (!oldData) return oldData
          return {
            data: [
              {
                ...newShortageOrder,
                product: {
                  id: newShortageOrder.product.id,
                  symbol: newShortageOrder.product.symbol,
                },
                createdAt: new Date(newShortageOrder.createdAt),
                fulfilledAt: newShortageOrder.fulfilledAt ? new Date(newShortageOrder.fulfilledAt) : null,
              },
              ...oldData.data,
            ],
            total: oldData.total + 1,
          }
        }
      )
    },
  })
}

export function useUpdateShortageOrder(props?: { isEmailModal?: boolean }) {
  const queryClient = useQueryClient()
  const isEmailModal = props?.isEmailModal ?? false

  return useMutation<ShortageOrder, QueryError, UpdateShortageOrder>({
    mutationKey: ['updateShortageOrder'],
    mutationFn: async (shortageOrder) => {
      return (await axios.put(`/shortage/order/${shortageOrder.id}`, shortageOrder)).data
    },
    meta: isEmailModal ? undefined : { successMessage: 'Zamówienie zostało zaktualizowane.', closeModal: true },
    onSuccess: (updatedShortageOrder) => {
      const { fulfilledAt, id, fulfilledAmount } = updatedShortageOrder
      queryClient.setQueriesData(
        { queryKey: ['shortages', 'orders'] },
        (oldData: Filterable<ShortageOrder> | undefined) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            data: oldData.data.map((item) => {
              if (item.id === id) {
                return {
                  ...updatedShortageOrder,
                  createdAt: new Date(updatedShortageOrder.createdAt),
                  fulfilledAt: updatedShortageOrder.fulfilledAt ? new Date(updatedShortageOrder.fulfilledAt) : null,
                }
              }
              return item
            }),
          }
        }
      )
      queryClient.invalidateQueries({ queryKey: ['shortages', 'orders'] })
      queryClient.invalidateQueries({ queryKey: ['shortages', 'grouped'] })
      // update the fulfilledAt date in the shortage query data
      queryClient.setQueriesData<Filterable<ShortageTableItem> | undefined>(
        { queryKey: ['shortages'], predicate: ({ queryKey }) => !queryKey.includes('grouped') },
        (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            data: oldData.data.map((item) => {
              if (item.shortageOrderId === id && item.fulfilledAt === null && fulfilledAt !== null) {
                return {
                  ...item,
                  fulfilledAt: new Date(fulfilledAt),
                  status: fulfilledAmount === -1 ? ShortageStatus.UNFULFILLED : ShortageStatus.FULFILLED,
                }
              }
              return item
            }),
          }
        }
      )
    },
  })
}

export function useAddShortagesToExistingOrder() {
  const queryClient = useQueryClient()

  return useMutation<ShortageOrder, QueryError, { shortageOrderId: string; shortageIds: Array<string> }>({
    mutationKey: ['addShortagesToExistingOrder'],
    mutationFn: async ({ shortageOrderId, shortageIds }) => {
      return (await axios.patch(`/shortage/order/${shortageOrderId}/addShortages`, { shortageIds })).data
    },
    meta: { successMessage: 'Braki zostały dodane do istniejącego zamówienia.', closeModal: true },
    onSuccess: (updatedShortageOrder, { shortageIds }) => {
      queryClient.setQueriesData<Filterable<ShortageTableItem> | undefined>(
        { queryKey: ['shortages'], predicate: ({ queryKey }) => !queryKey.includes('grouped') },
        (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            data: oldData.data.map((item) => {
              if (shortageIds.includes(item.id)) {
                return {
                  ...item,
                  status: getShortageStatus(updatedShortageOrder),
                  shortageOrderId: updatedShortageOrder.id,
                  fulfilledAt: updatedShortageOrder.fulfilledAt ? new Date(updatedShortageOrder.fulfilledAt) : null,
                }
              }
              return item
            }),
          }
        }
      )
      queryClient.setQueriesData<Filterable<ShortageOrderTableItem> | undefined>(
        { queryKey: ['shortages', 'orders'] },
        (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            data: oldData.data.map((item) => {
              if (item.id === updatedShortageOrder.id) {
                return {
                  ...updatedShortageOrder,
                  createdAt: new Date(updatedShortageOrder.createdAt),
                  fulfilledAt: updatedShortageOrder.fulfilledAt ? new Date(updatedShortageOrder.fulfilledAt) : null,
                }
              }
              return item
            }),
          }
        }
      )
      queryClient.invalidateQueries({ queryKey: ['shortages', 'grouped'] })
    },
  })
}

export function useDeleteShortageOrder() {
  const queryClient = useQueryClient()

  return useMutation<void, QueryError, string>({
    mutationKey: ['deleteShortageOrder'],
    mutationFn: async (id: string) => {
      await axios.delete(`/shortage/order/${id}`)
    },
    meta: { successMessage: 'Zamówienie produktu zostało usunięte.' },
    onSuccess: (_, id: string) => {
      queryClient.setQueriesData<Filterable<ShortageTableItem> | undefined>(
        {
          queryKey: ['shortages'],
          predicate: ({ queryKey }) => !queryKey.includes('grouped') && !queryKey.includes('orders'),
        },
        (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            data: oldData.data.map((item) => {
              if (item.shortageOrderId === id) {
                return {
                  ...item,
                  shortageOrderId: null,
                  orderedAt: null,
                  status: ShortageStatus.NEW,
                }
              }
              return item
            }),
          }
        }
      )
      queryClient.setQueriesData<Filterable<ShortageOrderTableItem> | undefined>(
        { queryKey: ['shortages', 'orders'] },
        (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            data: oldData.data.filter((item) => {
              if (item.id !== id) return item
            }),
          }
        }
      )
      queryClient.invalidateQueries({ queryKey: ['shortages', 'grouped'] })
    },
  })
}
